import React, { useEffect, useRef, useState } from 'react'

const confetti = Array.from({ length: 84 }, (_, index) => {
  const angle = (index * 137.508 * Math.PI) / 180
  const reach = 24 + ((index * 19) % 48)
  return {
    '--burst-x': `${Math.cos(angle) * reach}vw`,
    '--burst-y': `${Math.sin(angle) * reach - 24}vh`,
    '--fall-x': `${Math.cos(angle) * reach + ((index % 7) - 3) * 3}vw`,
    '--spin': `${(index % 2 ? 1 : -1) * (360 + (index % 5) * 180)}deg`,
    '--delay': `${520 + (index % 9) * 35}ms`,
    '--duration': `${2300 + (index % 7) * 170}ms`,
    '--piece-color': ['#b8a8ff', '#edff72', '#ffffff', '#7254ff', '#d4caff'][index % 5],
    '--piece-width': `${5 + (index % 4) * 2}px`,
  }
})

export default function QuestionnaireCelebration({ firstName, celebrate, onEstimate, onReview }) {
  const heading = useRef(null)
  const future = useRef(null)
  const page = useRef(null)
  const leaving = useRef(false)
  const animations = useRef([])
  const [departing, setDeparting] = useState(false)
  useEffect(() => () => animations.current.forEach((animation) => animation.cancel()), [])
  async function viewEstimate() {
    if (leaving.current) return
    leaving.current = true
    setDeparting(true)
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      onEstimate(null)
      return
    }
    animations.current = [...page.current.querySelectorAll('[data-estimate-fade]')].map((element) =>
      element.animate([{ opacity: getComputedStyle(element).opacity }, { opacity: 0 }], {
        duration: 480,
        easing: 'ease-in-out',
        fill: 'forwards',
      }),
    )
    await Promise.all(animations.current.map((animation) => animation.finished.catch(() => {})))
    if (!future.current) return
    const { left, top, width, height } = future.current.getBoundingClientRect()
    onEstimate({ left, top, width, height })
  }
  useEffect(() => {
    heading.current?.closest('main')?.scrollIntoView({ block: 'start', behavior: 'instant' })
    heading.current?.focus({ preventScroll: true })
  }, [])
  return (
    <div
      ref={page}
      className={`fabits-questionnaire-page q2-page q2-finale ${celebrate ? 'q2-celebrating' : ''}`}
    >
      <div className='q2-finale-atmosphere' aria-hidden='true'>
        <span className='q2-finale-glow' data-estimate-fade />
        <span className='q2-finale-orbit q2-orbit-one' data-estimate-fade />
        <span className='q2-finale-orbit q2-orbit-two' data-estimate-fade />
        <span className='q2-finale-orbit q2-orbit-three' data-estimate-fade />
      </div>
      {celebrate && (
        <div className='q2-confetti' aria-hidden='true' data-estimate-fade>
          {confetti.map((style, index) => (
            <i key={index} style={style} />
          ))}
        </div>
      )}
      <main className='q2-finale-main'>
        <section className='q2-finale-content' aria-labelledby='q2-finale-title'>
          <div className='q2-finale-emblem' aria-hidden='true' data-estimate-fade>
            <span className='q2-emblem-ring' />
            <span className='q2-emblem-disc'>
              <svg viewBox='0 0 80 80' fill='none'>
                <path
                  d='M22 41L34 53L59 27'
                  stroke='currentColor'
                  strokeWidth='5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </span>
            <span className='q2-emblem-star q2-star-one'>✦</span>
            <span className='q2-emblem-star q2-star-two'>✦</span>
            <span className='q2-emblem-star q2-star-three'>✧</span>
          </div>
          <p className='q2-finale-eyebrow' role='status' data-estimate-fade>
            Financial profile saved
          </p>
          <h1
            id='q2-finale-title'
            ref={heading}
            tabIndex={-1}
            aria-label='Your future. Taking shape.'
          >
            <span className='q2-finale-word' data-estimate-fade>
              Your{' '}
            </span>
            <span className='q2-finale-word q2-shared-future' ref={future}>
              future
            </span>
            <span className='q2-finale-word' data-estimate-fade>
              .
            </span>
            <br />
            <span data-estimate-fade>Taking shape.</span>
          </h1>
          <p className='q2-finale-personal' data-estimate-fade>
            A strong first draft, {firstName}.
          </p>
          <p className='q2-finale-description' data-estimate-fade>
            You’ve brought your financial picture together. Your answers are saved and ready for
            your next planning conversation.
          </p>
          <div className='q2-finale-actions' data-estimate-fade>
            <button
              type='button'
              className='q2-primary'
              onClick={viewEstimate}
              disabled={departing}
            >
              View estimate <span aria-hidden='true'>→</span>
            </button>
            <button
              type='button'
              className='q2-text-button'
              onClick={onReview}
              disabled={departing}
            >
              Review my answers
            </button>
          </div>
        </section>
      </main>
    </div>
  )
}
